import S, { DataSignal } from 's-js';
import * as Promise from 'bluebird';
import { debouncePromise } from 'lib/debouncePromise';
import { DraftSignal } from 'lib/draftSignals';
import TZDate from 'lib/TZDate';
import { LocalizationContext } from 'common/models/LocalizationContext';
import { tzmoment } from 'lib/tzmoment';
import { TZUtils } from 'lib/tzutils';
import { Error as ValidationError } from 'lib/validationWatcher';

import { DomainSummary } from 'data/models/DomainSummary';

// TODO: Update this union type to include 'area' to align with the new categorization scheme on the homepage's
// Traffic Schedule tab? Currently, ./view.tsx uses these values to do things like, if IN, show one set of
// labels, else if OUT show another, else if SHIFT show another. So it's really just a question about whether
// we wanna tweak those labels in light of the Traffic Schedule's new categorization scheme. Note that the only
// UI spots that use this EditMovementEndPopup widget currently are the Traffic Schedule's detailed
// MovementTimes widget, which we're planning to replace w/ the metro map anyway, and the PJO details.
export type MovementType = 'in' | 'out' | 'shift';

export type EditMovementEndPopup = ReturnType<typeof EditMovementEndPopup>;

export interface EditMovementEndPopupParams {
    EndTime : DataSignal<TZDate>;
    StartTime : DataSignal<TZDate>;
    MovementType : MovementType;
    OnClosePopup : () => void;
    Validate? : (startTime : TZDate, endTime : TZDate) => Promise<ValidationError[]>;
}

export function EditMovementEndPopup<Error>(
    Localization : LocalizationContext, 
    domain : DomainSummary, 
    tzutils : TZUtils,
    { 
        EndTime, 
        StartTime, 
        MovementType, 
        OnClosePopup,
        Validate = () => Promise.resolve([])
    } : EditMovementEndPopupParams
) {
    const DraftTime =  DraftSignal(EndTime, TZDate.eq),
        draftStartTime = DraftSignal(StartTime, TZDate.eq),
        KeepTimesLinked =  S.data(true),
        TransitDuration = transitDuration(StartTime, EndTime),
        NewTransitDurationIfUnlinked = transitDuration(StartTime, DraftTime),
        NewStartTimeIfLinked = () => {
            var newEndTime = DraftTime(),
                transitDur = TransitDuration();
    
            var newStartTime = tzutils.newDate(newEndTime.getTime() - transitDur * 60 * 1000);
    
            return newStartTime;
        },
        newStartTime = () => KeepTimesLinked() ? NewStartTimeIfLinked() : StartTime(),
        validation = debouncePromise(Validate, 300, []),
        Errors = S.data([] as ValidationError[]);

    // start validation process running, pipe output into Errors
    S(() => validation(newStartTime(), DraftTime()));
    S(() => validation.current().then(Errors));

    return {
        EndTime,
        StartTime,
        MovementType,
        Localization,
        DraftTime,
        KeepTimesLinked,
        TransitDuration,
        NewTransitDurationIfUnlinked,
        NewStartTimeIfLinked,
        OnClosePopup,
        SaveTimes,
        Errors,
        HasPilots: () => domain.HasActivePilots(),
    };

    function SaveTimes() {
        // checkpoint our changes at the point user clicks save, in case they change
        // before we finish validation
        draftStartTime(S.sample(newStartTime));

        const draftTimeSnapshot = DraftTime.checkpoint(),
            draftStartTimeSnapshot = draftStartTime.checkpoint();

        validation.flush().then(errors => S.freeze(() => {
            if (errors.length === 0) {
                draftTimeSnapshot.commit();
                draftStartTimeSnapshot.commit();
            
                if (OnClosePopup) OnClosePopup();
            }
        }));
    }

    function transitDuration(startTime : () => TZDate, endTime : () => TZDate) {
        return function () {
            return tzmoment(endTime()).diff(tzmoment(startTime()), 'minutes');
        };
    }
}


// WEBPACK FOOTER //
// ./apps/portcall/editmovementendpopup/model.ts